import { useState } from 'react';
import {
  formatLegalRegimes,
  intendedTreatmentLabel,
  sourceFormLabel,
} from '../../admission/labels';

function ProfileFact({ label, children, shaded }) {
  const empty = children === '—' || children == null || children === '';
  return (
    <div
      className={`flex items-start justify-between gap-4 border-b border-[#D1D5DB] px-4 py-3 last:border-b-0 ${
        shaded ? 'bg-[#F4F7FA]' : 'bg-white'
      }`}
    >
      <dt className="text-xs font-semibold text-[#4D4D4D]">{label}</dt>
      <dd
        className={`min-w-0 text-right ${
          empty
            ? 'text-[13px] font-medium text-[#8D8D8D]'
            : 'text-sm font-medium text-[#212121]'
        }`}
      >
        {children}
      </dd>
    </div>
  );
}

export function AdmissionProfileCard({ profile, compact = false }) {
  const [expanded, setExpanded] = useState(!compact);
  if (!profile) return null;

  const custodianName = profile.custodian?.name || '—';
  const evidenceCount = profile.required_evidence_count ?? 0;

  return (
    <section
      className="overflow-hidden rounded-xl border border-[#D1D5DB] bg-white"
      aria-labelledby="admission-profile-heading"
    >
      <div className="flex items-start justify-between gap-4 border-b border-[#D1D5DB] bg-white px-4 py-3">
        <div>
          <h3 id="admission-profile-heading" className="text-sm font-semibold text-[#003A70]">
            Programme Admission Profile
          </h3>
          <p className="mt-1 text-xs leading-4 text-[#4D4D4D]">
            Programme-derived and read-only. This is not an approval, authority, or admission
            decision.
          </p>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs font-semibold text-[#1F2933] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#003A70]/30 focus-visible:ring-offset-2"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Hide programme requirements' : 'View programme requirements'}
        </button>
      </div>

      {expanded ? (
        <dl>
          <ProfileFact label="Intended Treatment" shaded>
            {intendedTreatmentLabel(profile.intended_treatment)}
          </ProfileFact>
          <ProfileFact label="Primary Instrument Class">
            {profile.primary_instrument_label || '—'}
          </ProfileFact>
          <ProfileFact label="Applicable Legal Framework(s)" shaded>
            {formatLegalRegimes(profile.legal_regimes)}
          </ProfileFact>
          <ProfileFact label="Required Source Form">
            {sourceFormLabel(profile.required_source_form)}
          </ProfileFact>
          <ProfileFact label="Assigned Document Custodian" shaded>
            <span className="text-sm font-medium text-[#212121]">
              {custodianName}
              <span className="mt-1 block text-xs font-normal text-[#5A5A5A]">
                Designated by this programme. Custodian authority has not been approved.
              </span>
            </span>
          </ProfileFact>
          <ProfileFact label="Required Evidence">
            <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded border border-[#2E4D81] bg-[#EEF4FF] px-2 py-0.5 text-xs font-semibold text-[#003A70]">
              {evidenceCount}
            </span>
          </ProfileFact>
          <ProfileFact label="Profile Version" shaded>
            {profile.version ?? '—'}
          </ProfileFact>
          <ProfileFact label="Policy hash">
            <span className="font-mono text-[11px] font-normal break-all" title={profile.policy_hash}>
              {profile.policy_hash || '—'}
            </span>
          </ProfileFact>
        </dl>
      ) : (
        <p className="bg-[#F4F7FA] px-4 py-3 text-xs text-[#5A5A5A]">
          Version {profile.version ?? '—'}
          {profile.policy_hash ? ` · ${profile.policy_hash}` : ''}
        </p>
      )}
    </section>
  );
}
